import React, { useContext, useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import UserContext from "../../context/UserContext";
import UIContext from "../../context/UIContext";

// Render players as clickable tiles; clicking opens a small action menu for that player.
function LobbyPlayers({ lobbyData, onBlockUser, onSendPrivate, blockedUsers = [] }) {
  const { user } = useContext(UserContext);
  const { setShowUserProfileModal } = useContext(UIContext);
  const lobbyPlayers = lobbyData.lobbyPlayers || [];
  const lobbyTypingPlayers = lobbyData.lobbyTypingPlayers || [];

  // Ensure current user is shown at the top of the list
  const playersSorted = useMemo(() => {
    try {
      const list = Array.isArray(lobbyPlayers) ? [...lobbyPlayers] : [];
      const myIdx = list.findIndex((p) => p.username === (user && user.username));
      if (myIdx > -1) {
        const me = list.splice(myIdx, 1)[0];
        return [me, ...list];
      }
      return list;
    } catch (e) {
      return lobbyPlayers;
    }
  }, [lobbyPlayers, user && user.username]);

  const [openFor, setOpenFor] = useState(null); // socketId of player with menu open
  const menuRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null); // { top, left }

  useEffect(() => {
    function onDocClick(e) {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target)) setOpenFor(null);
    }
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const handleToggleMenu = (player, e) => {
    e.stopPropagation();
    const isOpen = openFor === player.socketId;
    if (isOpen) {
      setOpenFor(null);
      setMenuPos(null);
      return;
    }
    // compute position from the clicked element so we can render the menu in a portal
    try {
      const rect = e.currentTarget.getBoundingClientRect();
      const menuWidth = 224;
      const menuHeight = 140; // conservative estimate for flipping logic
      const offset = 6;
      // Prefer to show the menu below the button so clicking the button again will hide it.
      let top = rect.bottom + offset;
      // If not enough space below, flip above the button
      if (top + menuHeight > window.innerHeight - 8) {
        top = Math.max(8, rect.top - menuHeight - offset);
      }
      // Align menu's right edge with button's right edge where possible
      let left = rect.right - menuWidth;
      // If that would overflow the left side, align to the button's left edge
      if (left < 8) left = rect.left;
      // Ensure menu doesn't overflow the right edge of the viewport
      if (left + menuWidth > window.innerWidth - 8) left = Math.max(8, window.innerWidth - menuWidth - 8);
      setMenuPos({ top, left });
    } catch (err) {
      setMenuPos(null);
    }
    setOpenFor(player.socketId);
  };

  const handleBlock = (player) => {
    if (typeof onBlockUser === "function") onBlockUser(player.username);
    setOpenFor(null);
  };


  const handleSendPrivate = (player) => {
    if (typeof onSendPrivate === "function") onSendPrivate(player.userId || player.socketId, player.username);
    setOpenFor(null);
  };

  return (
    <div className="mb-4 px-2 sm:px-0 max-w-full overflow-x-auto">
      <div className="grid grid-cols-1 gap-2">
        {playersSorted.map((player) => {
          const showTyping = lobbyTypingPlayers.includes(player.username) && player.username !== user.username;
          const isCurrentUser = player.username === user.username;
          const isBlocked = blockedUsers.includes(player.username);
          return (
            <div key={player.socketId} className="relative">
              <div className={`w-full p-2 rounded flex items-center justify-between ${isCurrentUser ? 'bg-blue-50 dark:bg-blue-900' : 'bg-white dark:bg-gray-900'} border border-gray-200 dark:border-gray-700`}>
                <div className="flex items-center gap-2">
                  <span style={{ backgroundColor: player.color || '#e57373', borderRadius: '6px', padding: '6px 10px', display: 'inline-block', fontSize: '14px' }}>
                    {typeof player.icon === 'string' ? player.icon : '😀'}
                  </span>
                  <div>
                    <div className="flex items-center gap-2">
                        <div className="font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                          <span>{player.username}{isCurrentUser && ' (You)'}</span>
                          {isCurrentUser && (
                            <button
                              title="Edit profile"
                              onClick={(e) => { e.stopPropagation(); try { setShowUserProfileModal(true); } catch (err) {} }}
                              className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300"
                            >
                              <span className="text-xs font-medium text-gray-700 dark:text-gray-200">edit</span>
                            </button>
                          )}
                        </div>
                      {showTyping && (
                        <span className="inline-flex items-center gap-1">
                          <span className="w-2 h-2 rounded-full bg-gray-500 dark:bg-gray-400 animate-pulse" style={{ animationDelay: '0s' }} />
                          <span className="w-2 h-2 rounded-full bg-gray-500 dark:bg-gray-400 animate-pulse" style={{ animationDelay: '0.12s' }} />
                          <span className="w-2 h-2 rounded-full bg-gray-500 dark:bg-gray-400 animate-pulse" style={{ animationDelay: '0.24s' }} />
                        </span>
                      )}
                    </div>
                    {player.isInRoom && <div className="text-xs text-yellow-700 dark:text-yellow-300">In Game</div>}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {isBlocked && (
                    <span className="mr-2" title="Blocked">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" className="text-red-600 dark:text-red-400" aria-hidden="true"><path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12a9 9 0 1 0 18 0a9 9 0 1 0-18 0m2.7-6.3l12.6 12.6"/></svg>
                    </span>
                  )}
                  {/* Do not show the action button/menu for the current user's own tile */}
                  {1== 2 && !isCurrentUser && (
                    <button
                      aria-haspopup="true"
                      aria-expanded={openFor === player.socketId}
                      onClick={(e) => { e.stopPropagation(); handleToggleMenu(player, e); }}
                      className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300"
                      title="Actions"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="5" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="12" cy="19" r="1"/></svg>
                    </button>
                  )}
                </div>
              </div>
                
              {1 == 2 &&  !isCurrentUser && openFor === player.socketId && menuPos && createPortal(
                <div ref={menuRef} style={{ position: 'absolute', top: menuPos.top + 'px', left: menuPos.left + 'px', width: 224 }} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 text-gray-900 dark:text-gray-100">
                  <div className="px-2 py-2">
                    <button onClick={() => handleBlock(player)} className="flex items-center gap-2 w-full text-left px-3 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12a9 9 0 1 0 18 0a9 9 0 1 0-18 0m2.7-6.3l12.6 12.6"/></svg>
                      <span className="text-sm">{isBlocked ? 'Unblock Chat' : 'Block Chat'}</span>
                    </button>
                    {/* Vote Kick removed */}
                    { !(player.userId && user && user.userId && player.userId === user.userId) && (
                      <button onClick={() => handleSendPrivate(player)} className="flex items-center gap-2 w-full text-left px-3 py-2 mt-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17a2 2 0 1 0 4 0a2 2 0 1 0-4 0m5 5a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2m-3.546-2.03A9.9 9.9 0 0 1 7.7 19L3 20l1.3-3.9C1.976 12.663 2.874 8.228 6.4 5.726c3.526-2.501 8.59-2.296 11.845.48c1.667 1.423 2.596 3.294 2.747 5.216"/></svg>
                        <span className="text-sm">Send Private Message</span>
                      </button>
                    )}
                  </div>
                </div>, document.body)
              }


            </div>
          );
        })}
      </div>
    </div>
  );
}

export default LobbyPlayers;
